import React, {useEffect, useMemo} from 'react'

import {AnalyticsProvider} from '@github-ui/analytics-provider'
import type {FileOverviewPayload} from '@github-ui/code-view-types'
import {CurrentRepositoryProvider} from '@github-ui/current-repository'
import {AppPayloadContext} from '@github-ui/react-core/use-app-payload'
import {ScreenSize, ScreenSizeProvider} from '@github-ui/screen-size'
import {useNavigate} from '@github-ui/use-navigate'
import {Box} from '@primer/react'

// TODO move these imports to github-ui or react shared
// eslint-disable-next-line no-restricted-imports
import {CodeViewError} from '../../react-code-view/components/CodeViewError'
// eslint-disable-next-line no-restricted-imports
import FileTreeViewContent from '../../react-code-view/components/FileTreeViewContent'
// eslint-disable-next-line no-restricted-imports
import {CodeViewBannersProvider} from '../../react-code-view/contexts/CodeViewBannersContext'
// eslint-disable-next-line no-restricted-imports
import {FileQueryProvider} from '../../react-code-view/contexts/FileQueryContext'
// eslint-disable-next-line no-restricted-imports
import {AllShortcutsEnabledProvider} from '../../react-code-view/hooks/AllShortcutsEnabled'
// eslint-disable-next-line no-restricted-imports
import {FilesPageInfoProvider} from '../../react-code-view/hooks/FilesPageInfo'
// eslint-disable-next-line no-restricted-imports
import {useFilePagePayload} from '../../react-code-view/hooks/use-file-page-payload'

import {CurrentUserProvider} from '../../react-shared/Repos/CurrentUser'
import {useCanonicalObject} from '../../react-shared/hooks/use-canonical-object'
import {OverviewHeader} from './header/OverviewHeader'

export function ReposOverview({
  initialPayload,
  appPayload,
}: {
  initialPayload: FileOverviewPayload
  appPayload: unknown
}) {
  const payload = useFilePagePayload(initialPayload) as FileOverviewPayload

  const [repo] = React.useState(initialPayload?.repo)
  const [user] = React.useState(initialPayload?.currentUser)
  const refInfo = useCanonicalObject(payload.refInfo)

  const navigate = useNavigate()

  const appName = 'react-code-view-overview'
  const category = ''
  const metadata = useMemo(() => ({}), [])

  const onFindFilesShortcut = React.useCallback(() => {
    if (window.innerWidth < ScreenSize.large) {
      navigate(`${window.location.pathname}?search=1`)
    }
  }, [navigate])

  useEffect(() => {
    const footer = document.querySelector('.footer')
    if (footer) {
      footer.querySelector('.mt-6')?.classList.replace('mt-6', 'mt-0')
      footer.querySelector('.border-top')?.classList.remove('border-top')
    }
  }, [])

  return (
    <AppPayloadContext.Provider value={appPayload}>
      <ScreenSizeProvider initialValue={ScreenSize.xxxlarge}>
        <AnalyticsProvider appName={appName} category={category} metadata={metadata}>
          <CurrentUserProvider user={user}>
            <CurrentRepositoryProvider repository={repo}>
              <FilesPageInfoProvider
                refInfo={refInfo}
                path={payload.path}
                action="tree"
                copilotAccessAllowed={payload.copilotAccessAllowed ?? false}
              >
                <CodeViewBannersProvider>
                  <AllShortcutsEnabledProvider allShortcutsEnabled={payload.allShortcutsEnabled}>
                    <FileQueryProvider>
                      {payload.error ? (
                        <CodeViewError {...payload.error} />
                      ) : (
                        <Box sx={{my: 3}}>
                          <OverviewHeader payload={payload} />
                          <FileTreeViewContent
                            overview={payload.overview}
                            tree={payload.tree}
                            showTree={false}
                            treeToggleElement={null}
                            onFindFilesShortcut={onFindFilesShortcut}
                          />
                        </Box>
                      )}
                    </FileQueryProvider>
                  </AllShortcutsEnabledProvider>
                </CodeViewBannersProvider>
              </FilesPageInfoProvider>
            </CurrentRepositoryProvider>
          </CurrentUserProvider>
        </AnalyticsProvider>
      </ScreenSizeProvider>
    </AppPayloadContext.Provider>
  )
}

try{ ReposOverview.displayName ||= 'ReposOverview' } catch {}